// Wiederherstellungsprobe: die Rechnung hinter der Seite „Proben", dem
// Reiter „Probe" im Ketten-Blatt und dem Streifen auf der Backup-Seite
// (docs/restore-probe.md). EIN Ort für „wie steht die Probe, was zeigt die
// Leitung, was hieße das im Ernstfall" -- drei Stellen, eine Rechnung.
//
// Rein bis auf t(): alles, was sich bewegt (jetzt), kommt als Parameter.

import type { Halt, HaltZustand } from "../Leitung.svelte";
import type { Laeuft } from "../laeuft";
import type { LageTeil } from "../lage";
import { formatAbsoluteShort, formatBytes, formatDuration, formatZahl } from "../format";
import { t } from "../i18n.svelte";
import type { ProbeErgebnis, ProbeKette, ProbeLauf, ProbeSchritt } from "../backup-probe.svelte";
import { gesamtFortschritt } from "./gesamt";

export const PROBEN_PFAD = "/system/backups/proben";

export const zahl = (n?: number) => formatZahl(n ?? 0, 0);

/** „keys/forge" statt „forge": die Schlüssel-Ketten heißen nach ihrem
 *  Rechner, und in einem Satz über Rechner wäre „forge" missverständlich. */
export function kettenName(k: ProbeKette): string {
  return k.plan.gruppe ? `${k.plan.gruppe}/${k.plan.name}` : k.plan.name;
}

// ── Schritte ────────────────────────────────────────────────────────

export function schrittEtikett(id: string): string {
  switch (id) {
    case "kopie":
      return t("probe.step.copy", "Kopie");
    case "zurueck":
      return t("probe.step.restore", "Zurück");
    case "oeffnen":
      return t("probe.step.unlock", "Öffnen");
    case "pruefsummen":
      return t("probe.step.hashes", "Prüfsummen");
    case "vergleich":
      return t("probe.step.compare", "Vergleich");
    case "inhalt":
      return t("probe.step.content", "Inhalt");
    case "aufraeumen":
      return t("probe.step.cleanup", "Aufräumen");
  }
  return id;
}

function schrittOrt(k: ProbeKette, id: string): string {
  const p = k.plan;
  switch (id) {
    case "kopie":
      return p.kopie?.host ?? "—";
    case "zurueck":
      return p.weg === "werkzeug"
        ? t("probe.where.tool", "Tool → forge")
        : p.weg === "verzeichnis"
          ? t("probe.where.tar", "tar → forge")
          : t("probe.where.forge_in", "→ forge");
    case "vergleich":
      return p.referenz_art === "original"
        ? t("probe.where.original", "Original")
        : p.referenz_art === "erste_kopie"
          ? t("probe.where.first_copy", "1. Kopie")
          : t("probe.where.itself", "die Kopie");
    case "inhalt":
      return t("probe.where.dbs", "Datenbanken");
    case "aufraeumen":
      return "forge";
  }
  return "";
}

/** Kurz, damit es unter einen Halt passt: der Anteil zuerst. */
export function laeuftText(s: ProbeSchritt, jetztMs: number): string {
  const pct = s.anteil != null && s.anteil > 0 ? `${Math.floor(s.anteil * 100)} %` : "";
  if (s.einheit === "bytes" && s.wert != null) return [pct, formatBytes(s.wert)].filter(Boolean).join(" · ");
  if (s.einheit === "dateien" && pct) return pct;
  if (s.beginn) {
    const sek = Math.max(0, (jetztMs - new Date(s.beginn).getTime()) / 1000);
    return t("probe.running_for", "läuft {d}").replace("{d}", formatDuration(Math.round(sek)));
  }
  return t("probe.running", "läuft");
}

function fertigText(e: ProbeErgebnis, s: ProbeSchritt): string {
  switch (s.id) {
    case "kopie":
      return e.stand_zeit ? formatAbsoluteShort(e.stand_zeit) : "✓";
    case "zurueck":
      return e.zurueck_bytes ? formatBytes(e.zurueck_bytes) : "✓";
    case "oeffnen":
      return t("probe.key_ok", "Schlüssel passt");
    case "pruefsummen":
      return t("probe.n_files", "{n} Dateien").replace("{n}", zahl(e.seite_kopie?.dateien));
    case "vergleich":
      if (e.gegenprobe) return e.gegenprobe.erkannt ? t("probe.counter_found", "Gegenprobe erkannt") : "—";
      return e.gleich ? t("probe.equal", "gleich") : "—";
    case "inhalt": {
      const n = e.inhalt?.length ?? 0;
      return n === 0 ? t("probe.nothing_to_check", "nichts zu prüfen") : t("probe.n_ok", "{n} in Ordnung").replace("{n}", String(n));
    }
    case "aufraeumen":
      return t("probe.cleaned", "gelöscht");
  }
  return "✓";
}

function fehlerKurz(e: ProbeErgebnis, s: ProbeSchritt): string {
  if ((s.id === "vergleich" || s.id === "pruefsummen") && (e.abweichungen_anzahl ?? 0) > 0) {
    return t("probe.n_diffs", "{n} Abweichungen").replace("{n}", zahl(e.abweichungen_anzahl));
  }
  if (s.id === "inhalt") {
    const kaputt = (e.inhalt ?? []).filter((b) => !b.ok).length;
    if (kaputt > 0) return t("probe.n_broken", "{n} kaputt").replace("{n}", String(kaputt));
  }
  return t("probe.failed", "Fehler");
}

// ── Leitung ─────────────────────────────────────────────────────────
// Fünf Halte, nicht sieben: „Öffnen" gehört zum Zurückspielen, die
// Prüfsummen zum Vergleich. Mit sieben wurde die Zeile schon am
// Schreibtisch abgeschnitten und lief am Telefon über; im Detail stehen
// alle Schritte einzeln.
const HALTE: { id: string; schritte: string[] }[] = [
  { id: "kopie", schritte: ["kopie"] },
  { id: "zurueck", schritte: ["zurueck", "oeffnen"] },
  { id: "vergleich", schritte: ["pruefsummen", "vergleich"] },
  { id: "inhalt", schritte: ["inhalt"] },
  { id: "aufraeumen", schritte: ["aufraeumen"] },
];

/** Das Ergebnis, das eine Kette zeigt: im laufenden Durchgang ihr Live-
 *  Stand (sobald sie drankommt oder dran war), sonst ihr letztes Urteil. */
export function zeigeErgebnis(k: ProbeKette, lauf: ProbeLauf | null): { e?: ProbeErgebnis; imLauf: boolean } {
  const live = lauf?.ketten.find((x) => x.kette === k.plan.kette);
  if (live && (live.zustand === "laeuft" || live.zustand === "wartet")) return { e: live, imLauf: true };
  if (live && (live.zustand === "ok" || live.zustand === "fehler")) return { e: live, imLauf: false };
  return { e: k.letzter, imLauf: false };
}

export function halteVon(k: ProbeKette, lauf: ProbeLauf | null, jetztMs: number): { halte: Halt[]; fortschritt: number } {
  const { e, imLauf } = zeigeErgebnis(k, lauf);
  const schritte: ProbeSchritt[] = e?.halte?.length ? e.halte : k.plan.schritte.map((id) => ({ id, zustand: "wartet" }));
  let fortschritt = 0;
  const halte = HALTE.map((h, i): Halt => {
    const teile = schritte.filter((s) => h.schritte.includes(s.id));
    const kaputt = teile.find((s) => s.zustand === "fehler");
    const laeuft = teile.find((s) => s.zustand === "laeuft");
    const fertig = teile.filter((s) => s.zustand === "ok");
    const alleFertig = teile.length > 0 && fertig.length === teile.length;
    const zeigt = fertig.at(-1);
    let zustand: HaltZustand;
    let wann: string;
    if (!e) {
      zustand = i === 0 ? "quelle" : "wartet";
      wann = i === 0 ? (k.plan.kopie?.host ?? "") : "—";
    } else if (kaputt) {
      zustand = "fehler";
      wann = fehlerKurz(e, kaputt);
    } else if (imLauf && laeuft) {
      zustand = "aktiv";
      wann = laeuft.id === "oeffnen" ? t("probe.unlocking", "öffnet …") : laeuftText(laeuft, jetztMs);
      fortschritt = laeuft.anteil != null && laeuft.anteil > 0 ? laeuft.anteil : 0;
    } else if (alleFertig && zeigt) {
      zustand = i === 0 ? "quelle" : imLauf ? "fertig" : "da";
      wann = fertigText(e, h.id === "zurueck" ? teile[0] : zeigt);
      if (h.id === "zurueck" && e.versiegelt) wann += " · " + t("probe.unlocked", "geöffnet");
    } else if (imLauf && fertig.length > 0) {
      zustand = "aktiv";
      wann = t("probe.running", "läuft");
    } else if (imLauf && teile.every((s) => s.zustand === "wartet")) {
      zustand = "wartet";
      wann = t("probe.waiting", "wartet");
    } else {
      zustand = "ausgesetzt";
      wann = "—";
    }
    const titel = teile
      .map((s) => [schrittEtikett(s.id), s.text, s.dauer_s ? formatDuration(Math.round(s.dauer_s)) : ""].filter(Boolean).join(" · "))
      .join("\n");
    return { key: h.id, etikett: schrittEtikett(h.id), ort: schrittOrt(k, h.id), wann, zustand, titel };
  });
  return { halte, fortschritt };
}

// ── Kennzahlen eines Ergebnisses ────────────────────────────────────

/** Wie lange das Zurückspielen dauerte (Schritt „Zurück", ggf. mit Öffnen). */
export function zurueckDauer(e?: ProbeErgebnis): number | undefined {
  if (!e?.halte) return undefined;
  const s = e.halte.filter((h) => h.id === "zurueck" || h.id === "oeffnen").reduce((a, h) => a + (h.dauer_s ?? 0), 0);
  return s > 0 ? s : undefined;
}

/** Wie alt der zurückgespielte Stand war, als die Probe lief -- so viel
 *  ginge im Ernstfall verloren. */
export function standAlter(e?: ProbeErgebnis): number | undefined {
  if (!e?.stand_zeit || !e.beginn) return undefined;
  return Math.max(0, (new Date(e.beginn).getTime() - new Date(e.stand_zeit).getTime()) / 1000);
}

export function alterText(sek?: number): string {
  if (sek == null) return "—";
  const min = Math.round(sek / 60);
  if (min < 90) return `${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} d`;
}

export function inhaltText(e?: ProbeErgebnis): string {
  const dbs = (e?.inhalt ?? []).filter((b) => b.art === "sqlite").length;
  const arch = (e?.inhalt ?? []).filter((b) => b.art === "gzip").length;
  const teile: string[] = [];
  if (dbs) teile.push(t("probe.n_db_short", "{n} DB").replace("{n}", String(dbs)));
  if (arch) teile.push(t("probe.n_arch_short", "{n} Arch.").replace("{n}", String(arch)));
  return teile.join(" · ");
}

// ── Ernstfall ───────────────────────────────────────────────────────

export interface Ernstfall {
  geprueft: number;
  bestanden: number;
  nie: number;
  kaputt: ProbeKette[];
  /** Summe der Wiederherstellungen, s */
  dauer: number;
  /** ältester zurückgespielter Stand, s */
  datenstand?: number;
  schluessel?: { ok: boolean; text: string };
  bytes: number;
  dateien: number;
  dbs: number;
  archive: number;
  gegenprobe?: ProbeErgebnis;
  /** je Quell-Rechner: was im Ernstfall von wo zurückkommt */
  szenarien: { host: string; ketten: ProbeKette[]; ok: number; kaputt: number; nie: number; von: string[] }[];
}

export function ernstfall(ketten: ProbeKette[]): Ernstfall {
  const pruefbar = ketten.filter((k) => k.plan.pruefbar);
  const mit = pruefbar.filter((k) => k.letzter);
  const ef: Ernstfall = {
    geprueft: pruefbar.length,
    bestanden: mit.filter((k) => k.letzter?.zustand === "ok").length,
    nie: pruefbar.length - mit.length,
    kaputt: mit.filter((k) => k.letzter?.zustand === "fehler"),
    dauer: 0,
    bytes: 0,
    dateien: 0,
    dbs: 0,
    archive: 0,
    szenarien: [],
  };
  for (const k of mit) {
    const e = k.letzter!;
    ef.dauer += zurueckDauer(e) ?? 0;
    const a = standAlter(e);
    if (a != null && (ef.datenstand == null || a > ef.datenstand)) ef.datenstand = a;
    ef.bytes += e.zurueck_bytes ?? 0;
    ef.dateien += e.seite_kopie?.dateien ?? 0;
    ef.dbs += (e.inhalt ?? []).filter((b) => b.art === "sqlite").length;
    ef.archive += (e.inhalt ?? []).filter((b) => b.art === "gzip").length;
    if (e.versiegelt) ef.schluessel = { ok: e.zustand === "ok", text: e.schluessel ?? "" };
  }
  for (const k of ketten) {
    const g = k.gegenprobe;
    if (g?.ende && (!ef.gegenprobe?.ende || g.ende > ef.gegenprobe.ende)) ef.gegenprobe = g;
  }
  const je = new Map<string, ProbeKette[]>();
  for (const k of pruefbar) {
    const h = k.plan.quelle?.host ?? "—";
    je.set(h, [...(je.get(h) ?? []), k]);
  }
  ef.szenarien = [...je].map(([host, ks]) => ({
    host,
    ketten: ks,
    ok: ks.filter((k) => k.letzter?.zustand === "ok").length,
    kaputt: ks.filter((k) => k.letzter?.zustand === "fehler").length,
    nie: ks.filter((k) => !k.letzter).length,
    von: [...new Set(ks.map((k) => k.letzter?.kopie?.host ?? k.plan.kopie?.host ?? "—"))],
  }));
  ef.szenarien.sort((a, b) => b.ketten.length - a.ketten.length);
  return ef;
}

// ── Lage-Sätze ──────────────────────────────────────────────────────

export function ernstfallLage(ef: Ernstfall): LageTeil[] {
  if (ef.kaputt.length > 0) {
    return [
      { text: t("probe.lage.not_clean", "{name} kommt nicht sauber zurück").replace("{name}", ef.kaputt.map((k) => k.plan.name).join(", ")), ton: "danger" },
      { text: t("probe.lage.n_of_m_ok", "{n} von {m} ok").replace("{n}", String(ef.bestanden)).replace("{m}", String(ef.geprueft)) },
    ];
  }
  if (ef.geprueft > 0 && ef.bestanden === 0) return [{ text: t("probe.lage.never", "noch nie geprüft") }];
  const teile: LageTeil[] = [{ text: t("probe.lage.all_back", "alles kommt zurück"), ton: "ok" }];
  if (ef.nie > 0) teile[0] = { text: t("probe.lage.n_back", "{n} von {m} kommen zurück").replace("{n}", String(ef.bestanden)).replace("{m}", String(ef.geprueft)) };
  if (ef.dauer > 0) teile.push({ text: t("probe.lage.in", "in ≈ {d}").replace("{d}", formatDuration(Math.round(ef.dauer))) });
  if (ef.datenstand != null) teile.push({ text: t("probe.lage.loss", "höchstens {a} Datenverlust").replace("{a}", alterText(ef.datenstand)) });
  return teile;
}

export function kettenLage(ketten: ProbeKette[], lauf: ProbeLauf | null, naechste?: string): LageTeil[] {
  if (lauf && !lauf.ende) {
    const geprueft = lauf.ketten.filter((e) => e.pruefbar);
    const fertig = geprueft.filter((e) => e.zustand === "ok" || e.zustand === "fehler").length;
    const aktiv = lauf.ketten.find((e) => e.kette === lauf.aktiv);
    return [
      { text: aktiv ? t("probe.lage.running", "prüft {name}").replace("{name}", aktiv.name) : t("probe.lage.starting", "beginnt …"), ton: "accent" },
      { text: t("probe.lage.done_of", "{n} von {m} fertig").replace("{n}", String(fertig)).replace("{m}", String(geprueft.length)) },
    ];
  }
  const pruefbar = ketten.filter((k) => k.plan.pruefbar);
  const kaputt = pruefbar.filter((k) => k.letzter?.zustand === "fehler");
  const teile: LageTeil[] = [];
  if (kaputt.length) {
    teile.push({ text: t("probe.lage.n_failed", "{n} Fehler").replace("{n}", String(kaputt.length)), ton: "danger" });
    teile.push({ text: kaputt.map((k) => k.plan.name).join(", ") });
  } else {
    const ok = pruefbar.filter((k) => k.letzter?.zustand === "ok").length;
    teile.push(
      ok === pruefbar.length && ok > 0
        ? { text: t("probe.lage.all_ok", "alle bestanden"), ton: "ok" }
        : { text: t("probe.lage.n_of_m", "{n} von {m} bestanden").replace("{n}", String(ok)).replace("{m}", String(pruefbar.length)) },
    );
  }
  if (naechste) teile.push({ text: t("probe.lage.next", "nächste {zeit}").replace("{zeit}", formatAbsoluteShort(naechste)) });
  return teile;
}

/** Ein Eintrag für „läuft gerade" (Statusleiste, Baum, Telefon). Prüft der
 *  Lauf mehrere Ketten, steht der Fortschritt ÜBER ALLE da, gewichtet mit
 *  der Erfahrung (probe/gesamt.ts) -- „Proben 91 %", nicht die Kette, die
 *  gerade läuft. */
export function probeLaeuft(lauf: ProbeLauf | null, erfahrung: Record<string, number> = {}): Laeuft | undefined {
  if (!lauf || lauf.ende) return undefined;
  const g = gesamtFortschritt(lauf, erfahrung);
  if (!g) return undefined;
  const aktiv = lauf.ketten.find((e) => e.kette === lauf.aktiv);
  const fortschritt = Math.min(99, Math.round(g.anteil * 100));
  const was = aktiv ? t("probe.laeuft.was", "prüft {name}").replace("{name}", aktiv.name) : t("probe.laeuft.beginnt", "beginnt");
  const viele = g.ketten > 1;
  return {
    id: "probe",
    name: viele ? t("probe.laeuft.name_viele", "Proben") : t("probe.laeuft.name", "Probe"),
    was,
    fortschritt,
    pfad: PROBEN_PFAD,
    ton: "laeuft",
    titel: viele
      ? t("probe.laeuft.titel_viele", "Wiederherstellungsprobe: {p} % aller Ketten, {n} von {m} fertig, {was}")
          .replace("{p}", String(fortschritt))
          .replace("{n}", String(g.fertig))
          .replace("{m}", String(g.ketten))
          .replace("{was}", was)
      : t("probe.laeuft.titel", "Wiederherstellungsprobe {was} ({n} von {m} fertig)")
          .replace("{was}", was)
          .replace("{n}", String(g.fertig))
          .replace("{m}", String(g.ketten)),
  };
}

